/**
 * MOCK · CANONICAL (CT-5277) — entity rows per Unified Profile type.
 *
 * `visual` is null everywhere: an unreachable URL renders as a broken image
 * rather than the initials fallback (`Avatar` has no onError handling), which
 * made the list look broken in the browser while every test passed.
 *
 * Each type's rows use a DIFFERENT alias for the same normalized field. That is
 * not decoration: the mapper's whole job is to flatten those aliases into one
 * shape without branching on entity type, and a seed where every row already
 * looks identical would never exercise it.
 *
 *   customers   snake_case, fully populated, 24 rows (multi-page)
 *   employees   camelCase aliases (entityId / name / avatarUrl / summary)
 *   companies   snake_case, fully populated, 5 rows (CT-5523)
 *   vehicles    thinly mapped — title only, no type tag and no last interaction, so
 *               graceful degradation (and sorting rows with no recency) is testable
 *   dealerships published but empty
 */

// Last interaction is derived from a FIXED anchor, never `Date.now()`: the list
// is sorted by it, and a seed that moves with the clock makes order-dependent
// tests flaky. Offsets differ per type so the All view really interleaves them.
const ANCHOR_MS = Date.parse('2026-09-30T12:00:00.000Z');
const HOUR_MS = 60 * 60 * 1000;
const hoursAgo = (hours) => new Date(ANCHOR_MS - hours * HOUR_MS).toISOString();

// CT-5270 open question #11: does Michael's Entity List Figma map onto
// `EntityListCard` as shipped? Rows 1 and 2 carry the full field set from that
// prototype (source, code, Next Best Action, type tag) so the comparison can
// be checked live instead of read off a screenshot. Every other row omits
// them — same mapper, same component, fields that are simply absent.
const FULL_MOCK_ROWS = {
  1: {
    code: 'RO-48291',
    source: 'CDK Global',
    next_best_action: { label: 'Release bay 4 or reassign the order to Brandon', timestamp: '2h ago' },
    type_tag: 'Customer',
    badge: { label: 'Overdue', variant: 'danger' },
  },
  2: {
    code: 'PER-0104',
    source: 'Salesforce',
    next_best_action: { label: 'Send the migration timeline she asked for', timestamp: '30m ago' },
    type_tag: 'Customer',
    badge: { label: 'Active', variant: 'success' },
  },
};

const customerRow = (i) => {
  const full = FULL_MOCK_ROWS[i];
  return {
    entity_id: `c-${i}`,
    title: `Customer ${i}`,
    visual: null,
    description: `Account manager notes for customer ${i}.`,
    badges: [full?.badge ?? { label: i % 3 === 0 ? 'At risk' : 'Active', variant: i % 3 === 0 ? 'warning' : 'success' }],
    meta: [{ label: 'Region', value: i % 2 === 0 ? 'EMEA' : 'AMER' }],
    entity_type: 'customers',
    type_tag: 'Customer',
    email: `customer${i}@example.com`,
    last_interaction: hoursAgo(i * 7),
    ...(full
      ? { code: full.code, source: full.source, next_best_action: full.next_best_action, type_tag: full.type_tag }
      : {}),
  };
};

const employeeRow = (i) => ({
  entityId: `e-${i}`,
  name: `Employee ${i}`,
  avatarUrl: null,
  summary: `Field technician, cohort ${i}.`,
  badges: [{ label: 'Full time', variant: 'neutral' }],
  entityType: 'employees',
  typeTag: 'Employee',
  lastInteraction: hoursAgo(i * 11 + 3),
});

const COMPANY_NAMES = [
  ['Riverbend Auto Group', 'Automotive Retail', 'riverbend.example'],
  ['Northwind Health', 'Healthcare', 'northwind.example'],
  ['Meridian Corp', 'Enterprise Software', 'meridian.example'],
  ['Acme Logistics', 'Logistics', 'acme-logistics.example'],
  ['Helix Dynamics', 'Manufacturing', 'helix-dynamics.example'],
];

const companyRow = (i) => {
  const [name, industry, domain] = COMPANY_NAMES[i - 1];
  return {
    entity_id: `co-${i}`,
    title: name,
    visual: null,
    description: `${industry} account, key relationship ${i}.`,
    badges: [{ label: i === 4 ? 'Prospect' : 'Active', variant: i === 4 ? 'warning' : 'success' }],
    meta: [{ label: 'Industry', value: industry }],
    domain,
    code: `ORG-00${60 + i}`,
    source: 'CDK Global',
    entity_type: 'companies',
    type_tag: 'Company',
    last_interaction: hoursAgo(i * 13 + 20),
  };
};

const vehicleRow = (i) => ({
  id: `v-${i}`,
  label: `VIN 1HGCM82633A${100 + i}`,
});

const buildSeed = () => ({
  customers: Array.from({ length: 24 }, (_, idx) => customerRow(idx + 1)),
  employees: Array.from({ length: 8 }, (_, idx) => employeeRow(idx + 1)),
  companies: Array.from({ length: 5 }, (_, idx) => companyRow(idx + 1)),
  vehicles: Array.from({ length: 3 }, (_, idx) => vehicleRow(idx + 1)),
  dealerships: [],
});

let mockEntities = buildSeed();

export const getMockEntities = () => mockEntities;
export const setMockEntities = (next) => {
  mockEntities = next;
};

/**
 * A record created through the create flow goes at the head of its type: the
 * list is ordered by last interaction and a record born now is the freshest.
 */
export const appendMockEntity = (typeId, row) => {
  mockEntities = { ...mockEntities, [typeId]: [row, ...(mockEntities[typeId] ?? [])] };
};

/** The mock's "now": the seed anchor, so created records sort deterministically. */
export const MOCK_NOW_ISO = new Date(ANCHOR_MS).toISOString();

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockEntitiesForTests = () => {
  mockEntities = buildSeed();
};
